import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import api from '@/admin/lib/api'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { StaffShell } from '@/admin/components/staff/staff-shell'
import { SearchInput } from '@/shared/components/ui/search-input'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { useFormatters } from '@/shared/lib/formatting'
import type { PaginatedResponse } from '@/shared/types/api'

interface VoiceCall {
  uuid: string
  call_id: string
  tenant_name: string
  organisation_name: string
  consumer_email: string | null
  consumer_name: string
  source: string
  sentiment: string
  outcome: string
  successful: boolean | null
  needs_human_callback: boolean
  call_status: string
  duration_ms: number | null
  started_at: string | null
  created_at: string
  claim_uuid: string | null
}

function formatDuration(ms: number | null): string {
  if (!ms || ms <= 0) return '-'
  const total = Math.round(ms / 1000)
  const m = Math.floor(total / 60)
  const s = total % 60
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

function sentimentVariant(
  s: string,
): 'default' | 'secondary' | 'destructive' {
  switch ((s || '').toLowerCase()) {
    case 'positive':
      return 'default'
    case 'negative':
      return 'destructive'
    default:
      return 'secondary'
  }
}

const PAGE_SIZE = 25

export function VoiceCallsPage() {
  const navigate = useNavigate()
  const { formatDateTime } = useFormatters()
  const [searchParams, setSearchParams] = useSearchParams()

  const [query, setQuery] = useState(searchParams.get('q') ?? '')
  const [debouncedQuery, setDebouncedQuery] = useState(query)
  const [sentiment, setSentiment] = useState(searchParams.get('sentiment') ?? '')
  const [callback, setCallback] = useState(searchParams.get('callback') ?? '')
  const [page, setPage] = useState(Number(searchParams.get('page') ?? 1))

  useEffect(() => {
    const timer = setTimeout(() => {
      // Only reset paging when the user actually changed the query, so a
      // deep-linked ?page=N survives the initial mount.
      if (query !== debouncedQuery) {
        setDebouncedQuery(query)
        setPage(1)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [query, debouncedQuery])

  const { data, isPending, isFetching } = useQuery<PaginatedResponse<VoiceCall>>({
    queryKey: ['staff-voice-calls', page, debouncedQuery, sentiment, callback],
    queryFn: async () => {
      const params: Record<string, string> = {
        page: String(page),
        page_size: String(PAGE_SIZE),
      }
      if (debouncedQuery.length >= 2) params.search = debouncedQuery
      if (sentiment) params.sentiment = sentiment
      if (callback) params.needs_human_callback = callback
      const res = await api.get<PaginatedResponse<VoiceCall>>(
        '/staff/voice-calls/',
        { params },
      )
      return res.data
    },
    staleTime: 10_000,
  })

  useEffect(() => {
    const p: Record<string, string> = {}
    if (debouncedQuery) p.q = debouncedQuery
    if (sentiment) p.sentiment = sentiment
    if (callback) p.callback = callback
    if (page > 1) p.page = String(page)
    setSearchParams(p, { replace: true })
  }, [debouncedQuery, sentiment, callback, page, setSearchParams])

  const totalPages = data ? Math.ceil(data.count / PAGE_SIZE) : 0

  const columns: DataTableColumn<VoiceCall>[] = useMemo(
    () => [
      {
        id: 'tenant',
        header: 'Tenant',
        cell: (c) => (
          <div className="leading-tight">
            <p className="text-[var(--ink)]">{c.tenant_name || '-'}</p>
            {c.organisation_name ? (
              <p className="text-[11px] text-[var(--ink-3)]">
                {c.organisation_name}
              </p>
            ) : null}
          </div>
        ),
      },
      {
        id: 'consumer',
        header: 'Consumer',
        cell: (c) => (
          <span className="text-[var(--ink-2)]">
            {c.consumer_email || c.consumer_name || '-'}
          </span>
        ),
      },
      { id: 'outcome', header: 'Outcome', cell: (c) => c.outcome || '-' },
      {
        id: 'sentiment',
        header: 'Sentiment',
        cell: (c) =>
          c.sentiment ? (
            <Badge variant={sentimentVariant(c.sentiment)}>{c.sentiment}</Badge>
          ) : (
            <span className="text-[var(--ink-3)]">-</span>
          ),
      },
      {
        id: 'callback',
        header: 'Callback',
        cell: (c) =>
          c.needs_human_callback ? (
            <Badge variant="destructive">Callback</Badge>
          ) : (
            <span className="text-[var(--ink-3)]">-</span>
          ),
      },
      {
        id: 'duration',
        header: 'Duration',
        cell: (c) => (
          <span className="block text-right">{formatDuration(c.duration_ms)}</span>
        ),
      },
      {
        id: 'date',
        header: 'Date',
        cell: (c) => (
          <span className="block text-right text-[var(--ink-3)]">
            {c.started_at || c.created_at
              ? formatDateTime(c.started_at ?? c.created_at)
              : '-'}
          </span>
        ),
      },
    ],
    [formatDateTime],
  )

  return (
    <StaffShell>
      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="clara-title mb-1">Voice Calls</h1>
            <p className="text-[12px] text-[var(--ink-3)]">
              {data ? `${data.count} total` : '…'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={sentiment || 'all'}
              onValueChange={(v) => {
                setSentiment(v === 'all' ? '' : v)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Sentiment" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sentiment</SelectItem>
                <SelectItem value="Positive">Positive</SelectItem>
                <SelectItem value="Neutral">Neutral</SelectItem>
                <SelectItem value="Negative">Negative</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={callback || 'all'}
              onValueChange={(v) => {
                setCallback(v === 'all' ? '' : v)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Callback" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All calls</SelectItem>
                <SelectItem value="true">Callback requested</SelectItem>
                <SelectItem value="false">No callback</SelectItem>
              </SelectContent>
            </Select>
            <div className="w-64">
              <SearchInput
                value={query}
                onChange={setQuery}
                placeholder="Search call id, summary…"
              />
            </div>
          </div>
        </div>

        <DataTable<VoiceCall>
          tableId="staff-voice-calls"
          columns={columns}
          data={data?.results ?? []}
          onRowClick={(c) => navigate(`/staff/voice-calls/${c.uuid}`)}
          emptyMessage={
            debouncedQuery || sentiment || callback
              ? 'No voice calls match your filters'
              : 'No voice calls yet'
          }
          isLoading={isPending}
          isFetching={isFetching}
        />

        {data && data.count > 0 ? (
          <div className="mt-4 flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="icon-sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <span className="px-2 text-[11px] text-[var(--ink-3)]">
              {page} / {totalPages}
            </span>
            <Button
              variant="ghost"
              size="icon-sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : null}
      </div>
    </StaffShell>
  )
}
